import React, { useEffect, useRef, useState } from 'react';
import loginSx from './Login.styles';
import {
	Button,
	Card,
	FormControl,
	FormHelperText,
	Grid,
	IconButton,
	Input,
	InputAdornment,
	Link,
	Typography,
	useMediaQuery,
	Box,
} from "@mui/material";
import { Email, Lock, LockOpen, Visibility, VisibilityOff } from "@mui/icons-material";
import { Link as RouterLink } from '@tanstack/react-router';
import { useDispatch } from "react-redux";
import { actions } from "store";
import api from 'api';
import Cookies from 'js-cookie';
import { useTheme } from '@mui/material/styles';



const Login = () => {
	const theme = useTheme();
	const sx = loginSx(theme);
	const formRef = useRef(null);
	const dispatch = useDispatch();
	const isMdDown = useMediaQuery(theme.breakpoints.down('md'));

	const [ showPassword, setShowPassword ] = useState(false);
	const [ email, setEmail ] = useState('');
	const [ password, setPassword ] = useState('');
	const [ errors, setErrors ] = useState({ email: false, password: false });
	const [ loginError, setLoginError ] = useState(null);
	const [ loggingIn, setLoggingIn ] = useState(false);
	const [ finishedValidation, setFinishedValidation ] = useState(false);

	const handleToggleShowPassword = () => {
		setShowPassword(!showPassword);
	};


	const handleChangePassword = e => {
		setPassword(e.target.value);
	};


	const handleChangeEmail = e => {
		setEmail(e.target.value);
	};


	const handleSubmit = e => {
		e.preventDefault();

		setLoginError(null);

		setErrors({
			email: !Boolean(email),
			password: !Boolean(password),
		});

		if(email && password) {
			setLoggingIn(true);
			api.post('/auth/login', {
				user: email,
				password,
			}).then(res => {
				setLoggingIn(false);

				dispatch(actions.auth.setState({
					showLoginPage: false,
					showClinicSelection: res.data.showClinicSelection,
					clinics: res.data.clinics,
				}));

			}).catch(() => {
				setLoginError('Invalid username or password');
				setLoggingIn(false);
			});
		}
	};


	useEffect(() => {
		let token = Cookies.get('authToken');

		if(token) {
			setLoggingIn(true);
			api.get('/auth/validate-token').then(res => {
				dispatch(actions.auth.setState({
					showLoginPage: false,
					showClinicSelection: res.data.showClinicSelection,
					clinics: res.data.clinics,
				}));
			}).catch(err => {
				setLoggingIn(false);
				Cookies.set('authToken', '');
				setLoginError('Session expired, please log in again.');
				setFinishedValidation(true);
			});
		} else {
			setFinishedValidation(true);
		}


	}, [ setLoggingIn, setLoginError, dispatch, setFinishedValidation ]);


	if(!finishedValidation) {
		return null;
	}


	return (
		<Box sx={sx.root}>
			<Box sx={sx.child}>
				<Grid container spacing={0} sx={sx.fullHeightWidth}>
					{!isMdDown && (
						<Grid size={{ md: 5 }} sx={sx.leftCardWrapper}>
							<Card sx={sx.leftCard} raised>
								<Box sx={sx.leftCardInner}>
									<Box>
										<LockOpen sx={sx.leftCardIcon} />
									</Box>
									<Box>
										<Typography variant="h4" sx={sx.marginBottom}>Login</Typography>
									</Box>
									<Box>
										<Typography>
											Please log in to your account using the form to the right. If you have
											forgotten your password, please click <Link
											component={RouterLink}
											to="/forgot-password"
											color="inherit"
										>here</Link>.
										</Typography>
									</Box>

								</Box>
							</Card>
						</Grid>
					)}


					<Grid size={{ xs: 12, md: 7 }} sx={sx.rightCardWrapper}>
						<Card sx={sx.rightCard} raised>
							<form ref={formRef} onSubmit={handleSubmit}>
								<FormControl fullWidth margin="normal" sx={sx.inputs}>
									<Input
										disabled={loggingIn}
										type="text"
										placeholder="Email Address"
										onChange={handleChangeEmail}
										value={email}
										error={errors.email}
										startAdornment={
											<InputAdornment position="start">
												<Email />
											</InputAdornment>
										}
									/>
									<FormHelperText error>{errors.email && 'Email address is required'}</FormHelperText>
								</FormControl>
								<FormControl fullWidth margin="normal" sx={sx.inputs}>
									<Input
										disabled={loggingIn}
										type={showPassword
											? 'text'
											: 'password'}
										placeholder="Password"
										value={password}
										onChange={handleChangePassword}
										endAdornment={
											<InputAdornment position="end">
												<IconButton
													aria-label="Toggle password visibility"
													onClick={handleToggleShowPassword}
													size="large"
												>
													{showPassword
														? <Visibility />
														: <VisibilityOff />}
												</IconButton>
											</InputAdornment>
										}
										startAdornment={
											<InputAdornment position="start">
												<Lock />
											</InputAdornment>
										}
									/>
									<FormHelperText error>{errors.password && 'Password is required'}</FormHelperText>
								</FormControl>

								<FormControl fullWidth margin="normal">
									<Button
										variant="contained"
										color="primary"
										sx={sx.inputs}
										type="submit"
										disabled={loggingIn}
									>
										Log In
									</Button>
								</FormControl>
							</form>

							{loginError !== null && (
								<Box sx={{ ...sx.inputs, ...sx.error }}>
									{loginError}
								</Box>
							)}

							{isMdDown && (
								<Link
									component={RouterLink}
									to="/forgot-password"
									color="inherit"
								>
									Forgot password?
								</Link>
							)}

						</Card>
					</Grid>
				</Grid>
			</Box>
		</Box>
	);

};


export default Login;
